import { useEffect, useState, type ReactNode } from 'react';
import {
    analyticsApi,
    buildTableQuery,
    type AnalyticsFilters,
    type AnalyticsTable,
    type AnalyticsTableName,
    type TableParams,
} from '../../services/analytics';
import { DEFAULT_ACCOUNT_SORT, type AccountColumn } from './accountTables';
import type { SortState } from './tableFilter';
import { DataTable } from './DataTable';

interface AccountTableCardProps {
    name: AnalyticsTableName;
    title: ReactNode;
    subtitle?: string;
    /** Parametros proprios da tabela (ex.: kind e days de Contas sem atividade). */
    params?: TableParams;
    /** Controle a direita do titulo (ex.: seletor de evento). */
    headerAction?: ReactNode;
    renderHeader?: (table: AnalyticsTable | null) => ReactNode;
    /** Sem isso, usa as colunas que o back-end mandou. */
    columns?: AccountColumn[];
    formatters?: Record<string, (value: unknown) => string>;
    /** Ordenacao inicial; sem isso, por cliente. */
    defaultSort?: SortState;
    exportFilename: string;
    emptyMessage: string;
    filters: AnalyticsFilters;
    /** Falso enquanto o periodo personalizado esta incompleto: nada e consultado. */
    enabled: boolean;
    /** Muda quando a pessoa pede "tentar novamente" no painel. */
    refreshKey: number;
    timeZone: string;
}

interface Settled {
    key: string;
    table: AnalyticsTable | null;
    error: string | null;
}

/** Cartao com uma tabela do painel: busca, filtro, ordenacao pelo cabecalho e exportacao em Excel. */
export function AccountTableCard({
    name,
    title,
    subtitle,
    params,
    headerAction,
    renderHeader,
    columns,
    formatters,
    defaultSort = DEFAULT_ACCOUNT_SORT,
    exportFilename,
    emptyMessage,
    filters,
    enabled,
    refreshKey,
    timeZone,
}: AccountTableCardProps) {
    const [retry, setRetry] = useState(0);
    const [settled, setSettled] = useState<Settled | null>(null);

    const requestKey = enabled ? `${name}:${buildTableQuery(filters, params)}#${refreshKey}.${retry}` : null;

    useEffect(() => {
        if (!requestKey) return;
        const controller = new AbortController();
        analyticsApi
            .table(name, filters, params, controller.signal)
            .then((table) => setSettled({ key: requestKey, table, error: null }))
            .catch((e: unknown) => {
                if (controller.signal.aborted) return;
                setSettled({
                    key: requestKey,
                    table: null,
                    error: e instanceof Error ? e.message : 'Não foi possível carregar as contas.',
                });
            });
        return () => controller.abort();
        // requestKey resume tabela, filtros e contadores de nova tentativa.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestKey]);

    const loading = requestKey !== null && settled?.key !== requestKey;
    const current = settled && settled.key === requestKey ? settled : null;
    // Mantem a ultima tabela na tela enquanto a nova carrega, para nao piscar.
    const table = current ? current.table : (settled?.table ?? null);

    return (
        <DataTable
            card
            title={title}
            subtitle={subtitle}
            hint={headerAction}
            header={renderHeader?.(table)}
            table={table}
            columns={columns}
            formatters={formatters}
            defaultSort={defaultSort}
            loading={loading}
            error={current?.error ?? null}
            onRetry={() => setRetry((n) => n + 1)}
            timeZone={timeZone}
            emptyMessage={emptyMessage}
            exportFilename={exportFilename}
        />
    );
}
